import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import camelcaseKeys from 'camelcase-keys';
import ContactAPI from 'dashboard/api/contacts';

const CHANNEL_PRIORITY = {
  'Channel::Email': 1,
  'Channel::Whatsapp': 2,
  'Channel::Sms': 3,
  'Channel::TwilioSms': 4,
  'Channel::SmsBrana': 4,
  'Channel::Didww': 4,
  'Channel::WebWidget': 5,
  'Channel::Api': 6,
};

export const hasMessageContent = (message = '') => {
  if (!message) return false;
  if (/<img\s/i.test(message)) return true;
  return !!message.replace(/<[^>]*>/g, '').trim();
};

export const hasDraftContent = ({
  message = '',
  subject = '',
  ccEmails = '',
  bccEmails = '',
  files = [],
  uploadingFiles = [],
}) => {
  const hasFiles = files?.length > 0 || uploadingFiles?.length > 0;
  return !!(
    hasMessageContent(message) ||
    subject?.trim() ||
    ccEmails?.trim() ||
    bccEmails?.trim() ||
    hasFiles
  );
};

export const generateLabelForContactableInboxesList = ({
  name,
  internalName,
  email,
  channelType,
  phoneNumber,
}) => {
  let labelName = name;
  if (internalName && internalName.length > 0) {
    labelName = internalName;
  }
  if (channelType === INBOX_TYPES.EMAIL) {
    return `${labelName} (${email})`;
  }
  if (
    (channelType === INBOX_TYPES.TWILIO ||
      channelType === INBOX_TYPES.WHATSAPP ||
      channelType === INBOX_TYPES.SMS_BRANA ||
      channelType === INBOX_TYPES.DIDWW) &&
    phoneNumber?.length > 0
  ) {
    return `${labelName} (${phoneNumber})`;
  }
  return labelName;
};

const transformInbox = ({
  name,
  internalName,
  id,
  email,
  channelType,
  phoneNumber,
  senderEmail,
  ...rest
}) => ({
  id,
  icon: getInboxIconByType(channelType, phoneNumber, 'line'),
  label: generateLabelForContactableInboxesList({
    name,
    internalName,
    email,
    channelType,
    phoneNumber,
  }),
  action: 'inbox',
  value: senderEmail ? `${id}-${senderEmail}` : id,
  name,
  email,
  phoneNumber,
  channelType,
  ...(senderEmail ? { senderEmail } : {}),
  ...rest,
});

const expandEmailInboxBySenderEmails = inbox => {
  if (inbox.channelType !== INBOX_TYPES.EMAIL) return [inbox];

  const aliasEmails = (inbox.emailAliases || []).map(
    emailAlias => emailAlias.email
  );
  if (aliasEmails.length === 0) return [inbox];

  const primary = inbox.email;
  const senderEmails = [primary, ...aliasEmails].filter(Boolean);

  return senderEmails.map(senderEmail => ({
    ...inbox,
    email: senderEmail,
    senderEmail,
  }));
};

export const compareInboxes = (a, b) => {
  // Channels that have no priority defined should come at the end.
  const priorityA = CHANNEL_PRIORITY[a.channelType] || 999;
  const priorityB = CHANNEL_PRIORITY[b.channelType] || 999;

  if (priorityA !== priorityB) {
    return priorityA - priorityB;
  }

  const labelA = a.label || '';
  const labelB = b.label || '';
  return labelA.localeCompare(labelB);
};

export const buildContactableInboxesList = contactInboxes => {
  if (!contactInboxes) return [];

  return contactInboxes
    .flatMap(expandEmailInboxBySenderEmails)
    .map(transformInbox)
    .sort(compareInboxes);
};

export const getCapitalizedNameFromEmail = email => {
  const name = email.match(/^([^@]*)@/)?.[1] || email.split('@')[0];
  return name.charAt(0).toUpperCase() + name.slice(1);
};

export const processContactableInboxes = inboxes => {
  return inboxes.map(inbox => ({
    ...inbox.inbox,
    sourceId: inbox.sourceId,
  }));
};

export const mergeInboxDetails = (inboxesData, inboxesList = []) => {
  if (!inboxesData || !inboxesData.length) {
    return [];
  }

  return inboxesData.map(inboxData => {
    const matchingInbox =
      inboxesList.find(inbox => inbox.id === inboxData.id) || {};
    return {
      ...camelcaseKeys(matchingInbox, { deep: true }),
      ...inboxData,
    };
  });
};

export const prepareNewMessagePayload = ({
  targetInbox,
  selectedContact,
  message,
  subject,
  ccEmails,
  bccEmails,
  toEmails,
  resolved = false,
  isInternal = false,
}) => {
  const payload = {
    inboxId: targetInbox.id,
    sourceId: targetInbox.sourceId,
    contactId: Number(selectedContact.id),
    message: { content: message },
    assigneeId: null,
  };

  if (targetInbox.channelType === 'Channel::Email') {
    payload.message.content_attributes = {
      html_content: message,
    };
  }

  if (subject) {
    payload.mailSubject = subject;
  }

  if (ccEmails) {
    payload.message.cc_emails = ccEmails;
  }

  if (bccEmails) {
    payload.message.bcc_emails = bccEmails;
  }

  if (toEmails) {
    payload.message.to_emails = toEmails;
  }

  if (targetInbox.senderEmail) {
    payload.message.from_email = targetInbox.senderEmail;
  }

  if (isInternal) {
    payload.message.private = true;
  }

  payload.status = resolved ? 'resolved' : 'open';
  return payload;
};

export const prepareWhatsAppMessagePayload = ({
  targetInbox,
  selectedContact,
  message,
  templateParams,
  currentUser,
}) => {
  return {
    inboxId: targetInbox.id,
    sourceId: targetInbox.sourceId,
    contactId: selectedContact.id,
    message: { content: message, template_params: templateParams },
    assigneeId: currentUser.id,
  };
};

export const generateContactQuery = ({ keys = ['email'], query }) => {
  return {
    payload: keys.map(key => {
      const filterPayload = {
        attribute_key: key,
        filter_operator: 'contains',
        values: [query],
        attribute_model: 'standard',
      };
      if (keys.findIndex(k => k === key) !== keys.length - 1) {
        filterPayload.query_operator = 'or';
      }
      return filterPayload;
    }),
  };
};

// API Calls
export const searchContacts = async ({ keys, query }) => {
  const {
    data: { payload },
  } = await ContactAPI.filter(
    undefined,
    'name',
    generateContactQuery({ keys, query })
  );
  const camelCasedPayload = camelcaseKeys(payload, { deep: true });
  // Filter contacts that have either phone_number or email
  const filteredPayload = camelCasedPayload?.filter(
    contact => contact.phoneNumber || contact.email
  );
  return filteredPayload || [];
};

export const createNewContact = async input => {
  const trimmed = (input || '').trim();
  const payload = {
    name: trimmed.startsWith('+')
      ? trimmed.slice(1) // Remove the '+' prefix if it exists
      : getCapitalizedNameFromEmail(trimmed),
    ...(trimmed.startsWith('+')
      ? { phone_number: trimmed }
      : { email: trimmed }),
  };

  const {
    data: {
      payload: { contact: newContact },
    },
  } = await ContactAPI.create(payload);

  return camelcaseKeys(newContact, { deep: true });
};

// Resolves an email address to a contact, reusing the existing one when the
// address is already known. Contacts::CreationService dedupes on
// email -> phone_number -> identifier and returns the stored contact untouched,
// so repeated deep links never create duplicates.
export const findOrCreateContact = async email => {
  const trimmed = (email || '').trim();
  const {
    data: {
      payload: { contact },
    },
  } = await ContactAPI.findOrCreate({
    email: trimmed.toLowerCase(),
    name: getCapitalizedNameFromEmail(trimmed),
  });

  return camelcaseKeys(contact, { deep: true });
};

export const fetchContactableInboxes = async contactId => {
  const {
    data: { payload: inboxes = [] },
  } = await ContactAPI.getContactableInboxes(contactId);

  const convertInboxesToCamelKeys = camelcaseKeys(inboxes, { deep: true });

  return processContactableInboxes(convertInboxesToCamelKeys);
};
